import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errText, openAuthedFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { SignaturePad } from '@/components/SignaturePad';
import FilledFormPreview from '@/components/FilledFormPreview';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import {
  Zap, CheckCircle2, ExternalLink, AlertTriangle, ScanLine, ListChecks,
  Keyboard, ChevronRight, CalendarClock, Paperclip,
} from 'lucide-react';
import { cn } from '@/lib/utils';

function FactRow({ label, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b last:border-b-0" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground pt-0.5 shrink-0 w-28">{label}</div>
      <div className="text-sm font-medium text-right flex-1 min-w-0">{children}</div>
    </div>
  );
}

/**
 * One employer cure (LB-CURE): an already-delivered packet an employer called
 * insufficient, with 29 CFR 825.305(c)'s seven-day clock running on it.
 *
 * Everything the doctor needs is on the card — what was asked, the patient's own
 * account of it, a box for the answer — and answering plus re-signing is ONE
 * request (`cure_response` rides on the sign payload). That is the design
 * constraint, not a convenience: every extra step here is a day of somebody's
 * leave protection.
 *
 * Its own signature pad rather than the carousel's, because a doctor may have
 * cures and nothing to sign, and this card has to work on a page with no
 * carousel on it at all.
 */
function CureCard({ item, onDone }) {
  const [response, setResponse] = useState('');
  const [signature, setSignature] = useState(null);
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const req = (item.requests || [])[0] || {};
  const left = req.days_left;
  const urgent = req.urgency === 'overdue' || req.urgency === 'today';
  const countdown = req.urgency === 'overdue' ? 'Deadline passed'
    : req.urgency === 'today' ? 'Due today'
    : left === 1 ? '1 day left' : `${left} days left`;

  const submit = async () => {
    if (!response.trim() || !signature || !attest || busy) return;
    setBusy(true);
    try {
      await api.post(`/submissions/${item.id}/sign`, {
        signature_data_url: signature, confirm: true, cure_response: response.trim(),
      });
      toast.success(`Answered and re-signed — ${item.patient_name}.`);
      onDone?.();
    } catch (e) {
      toast.error(errText(e, 'Could not re-sign'));
    } finally { setBusy(false); }
  };

  return (
    <Card className={cn('border', urgent && 'border-[hsl(45_93%_74%)]')} data-testid={`cure-card-${item.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarClock className={cn('h-4 w-4', urgent ? 'text-[hsl(32_92%_38%)]' : 'text-muted-foreground')} />
          <CardTitle className="text-sm font-semibold">{item.patient_name || 'Patient'}</CardTitle>
          {item.employer_name && <span className="text-xs text-muted-foreground">· {item.employer_name}</span>}
          <Badge
            className={cn('text-xs', urgent && 'bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]')}
            variant={urgent ? undefined : 'secondary'}
            data-testid={`cure-countdown-${item.id}`}
          >
            {countdown}
          </Badge>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => window.open(`/doctor/review/${item.id}`, '_blank')} data-testid={`cure-open-chart-${item.id}`}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Full chart
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border bg-muted/40 p-3 space-y-1.5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Employer asked for</div>
          <ul className="text-sm list-disc pl-5 space-y-0.5" data-testid={`cure-asked-${item.id}`}>
            {(req.missing || []).map((m) => <li key={m}>{m}</li>)}
          </ul>
          {req.message && <p className="text-sm text-muted-foreground leading-6">“{req.message}”</p>}
          {req.notice_date && <p className="text-xs text-muted-foreground">Employer notice dated {req.notice_date}</p>}
          {req.letter?.upload_id && (
            <Button
              variant="outline" size="sm" className="mt-1"
              onClick={() => openAuthedFile(`/uploads/${item.id}/${req.letter.upload_id}`, 'employer-letter')}
              data-testid={`cure-letter-${item.id}`}
            >
              <Paperclip className="h-3.5 w-3.5 mr-1.5" /> View HR's letter
            </Button>
          )}
        </div>
        <div>
          <label className="text-sm font-medium" htmlFor={`cure-response-${item.id}`}>Your response to the employer</label>
          <Textarea
            id={`cure-response-${item.id}`}
            rows={4}
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder="The additional information the employer asked for. This goes into the amended certification, above your signature."
            className="mt-1"
            data-testid={`cure-response-${item.id}`}
          />
        </div>
        <SignaturePad onChange={setSignature} />
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={attest} onCheckedChange={(v) => setAttest(!!v)} data-testid={`cure-attest-${item.id}`} />
          <span className="leading-5 text-muted-foreground">
            I certify the additional information above is accurate and based on my examination of this patient.
          </span>
        </label>
        <Button
          className="w-full"
          disabled={!response.trim() || !signature || !attest || busy}
          onClick={submit}
          data-testid={`cure-sign-${item.id}`}
        >
          {busy ? 'Sending…' : 'Answer & re-sign'}
        </Button>
      </CardContent>
    </Card>
  );
}


function CureStack({ cures, onDone }) {
  if (!cures?.length) return null;
  return (
    <div className="space-y-3 mb-6" data-testid="cure-stack">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-[hsl(32_92%_38%)]" />
        <h2 className="text-sm font-semibold tracking-tight">Employer needs more information</h2>
        <Badge variant="secondary" data-testid="cure-count">{cures.length}</Badge>
        <span className="text-xs text-muted-foreground">Seven-day deadline — answer and re-sign here.</span>
      </div>
      {cures.map((c) => <CureCard key={c.id} item={c} onDone={onDone} />)}
    </div>
  );
}


// §2 doctor note (2026-09-06): the attestation reads "for every case I sign in
// this session", so it lives for the browser session — a reload mid-queue no
// longer clears it, closing the tab does. Per-viewer convenience only; the
// server never sees it.
const ATTEST_KEY = 'fmla_fastsign_attest_v1';
function readAttest() { try { return sessionStorage.getItem(ATTEST_KEY) === '1'; } catch { return false; } }
function writeAttest(v) { try { if (v) sessionStorage.setItem(ATTEST_KEY, '1'); else sessionStorage.removeItem(ATTEST_KEY); } catch { /* private mode */ } }

export default function DoctorFastSign() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [queue, setQueue] = useState(null); // null = loading
  const [idx, setIdx] = useState(0);
  const [signature, setSignature] = useState(null);
  const [attest, setAttestState] = useState(readAttest); // once per session
  const setAttest = (v) => { writeAttest(v); setAttestState(v); };
  const [busy, setBusy] = useState(false);
  const [cures, setCures] = useState([]);
  // The signature saved on the doctor's profile (`/me/signature`). "Draw it once
  // — it applies to every case you sign" used to be true only until a reload.
  const [savedSig, setSavedSig] = useState(null);
  const [editingSig, setEditingSig] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/me/signature').then((r) => {
      const url = r.data?.signature_data_url;
      if (alive && url) { setSavedSig(url); setSignature(url); }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const forgetSignature = async () => {
    try {
      await api.delete('/me/signature');
      setSavedSig(null); setSignature(null); setEditingSig(false);
      toast.success('Saved signature removed');
    } catch (e) { toast.error(errText(e, 'Could not remove the saved signature')); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/doctor/fast-sign/queue');
      setQueue(r.data.cases || []);
      setIdx(0);
    } catch (e) {
      toast.error(errText(e, 'Could not load fast-sign queue'));
      setQueue([]);
    }
    // LB-CURE. A separate call because these cases are NOT awaiting_doctor — a
    // delivered packet may only become AMENDED, so the cure loop reaches this
    // screen by query. Failing to load them must not take the sign queue down.
    try {
      const c = await api.get('/doctor/cures');
      setCures(c.data.cases || []);
    } catch { setCures([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = queue && queue.length > 0 ? queue[Math.min(idx, queue.length - 1)] : null;
  const revisionBlocked = (current?.open_revision_count || 0) > 0;
  // ADR 0009: part of that count may be the doctor's own question to the patient.
  const questionOnly = revisionBlocked && (current?.open_question_count || 0) >= current.open_revision_count;
  const canSign = !!(current && signature && attest && !busy && !revisionBlocked);

  const signAndNext = useCallback(async () => {
    if (!current || !signature || !attest || busy) return;
    if ((current.open_revision_count || 0) > 0) {
      toast.warning(questionOnly
        ? 'You asked this patient a question — open the full chart to see their answer first.'
        : 'This patient requested a change — open the full chart to review it first.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${current.id}/sign`, { signature_data_url: signature, confirm: true });
      const remaining = queue.filter((c) => c.id !== current.id);
      setQueue(remaining);
      setIdx((i) => Math.min(i, Math.max(remaining.length - 1, 0)));
      toast.success(`Signed — ${current.patient_name}. ${remaining.length} remaining.`);
      // A signature the server just accepted is one worth remembering. Fire and
      // forget: the case is signed whether or not this lands (inviolable rule 4).
      if (signature !== savedSig) {
        api.put('/me/signature', { signature_data_url: signature })
          .then(() => { setSavedSig(signature); setEditingSig(false); })
          .catch(() => {});
      }
    } catch (e) {
      toast.error(errText(e, 'Sign failed'));
    } finally { setBusy(false); }
  }, [current, signature, attest, busy, queue, savedSig, questionOnly]);

  // Keyboard shortcuts: Enter = sign & next, J/K = next/prev, O = open full chart
  useEffect(() => {
    const onKey = (e) => {
      // Fire the shortcuts only when nothing is focused and no dialog is open, so
      // Enter on a focused button, checkbox, Select or dialog field does its own
      // action and can never accidentally SIGN the packet (a signature is
      // permanent) — GAPS F19. Ignore auto-repeat too.
      const ae = document.activeElement;
      if (ae && ae !== document.body) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.repeat) return;
      if (e.key === 'Enter') { e.preventDefault(); signAndNext(); }
      else if (e.key === 'j' || e.key === 'J') setIdx((i) => Math.min(i + 1, (queue?.length || 1) - 1));
      else if (e.key === 'k' || e.key === 'K') setIdx((i) => Math.max(i - 1, 0));
      else if ((e.key === 'o' || e.key === 'O') && current) navigate(`/doctor/review/${current.id}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [signAndNext, queue, current, navigate]);

  if (queue === null) {
    return (
      <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-9 w-56" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="p-4 lg:p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Zap className="h-5 w-5 text-primary" />
          <h1 className="text-base lg:text-lg font-semibold tracking-tight">Fast-Sign</h1>
        </div>
        {/* Cures live OUTSIDE the sign queue, so "nothing to sign" is not the same
            as "nothing to do" — and the thing with a legal deadline on it is
            exactly what must not be hidden behind an all-caught-up card. */}
        <CureStack cures={cures} onDone={load} />
        {cures.length > 0 ? null : (
        <Card className="max-w-lg mx-auto mt-12" data-testid="fast-sign-empty-state">
          <CardContent className="p-10 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <div className="text-lg font-semibold">You're all caught up</div>
            <p className="text-sm text-muted-foreground">Nothing is waiting for your signature right now.</p>
            <Button variant="outline" onClick={() => navigate('/doctor')} data-testid="fast-sign-back-to-queue">
              <ListChecks className="h-4 w-4 mr-1.5" /> Go to Review Queue
            </Button>
          </CardContent>
        </Card>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Zap className="h-5 w-5 text-primary" />
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Fast-Sign</h1>
        <Badge variant="secondary" data-testid="fast-sign-count">{queue.length} ready</Badge>
        <div className="flex-1" />
        <span className="hidden lg:inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Keyboard className="h-3.5 w-3.5" /> Enter = sign · J/K = next/prev · O = full chart
        </span>
        <Button variant="outline" size="sm" onClick={() => navigate('/doctor')} data-testid="fast-sign-full-queue-link">
          <ListChecks className="h-4 w-4 mr-1.5" /> Full queue
        </Button>
      </div>

      <CureStack cures={cures} onDone={load} />

      {/* §2 doctor note (2026-09-06): on a phone the sign rail sat under the case
          card AND the "Up next" list, so every case was a scroll. DOM order is now
          card → rail → queue; on lg the rail is placed in the right column
          explicitly, so desktop looks exactly as before. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 items-start">
        {/* ------- Case facts (5 facts, glanceable in <5s) ------- */}
        <div className="lg:col-start-1 lg:row-start-1">
          <div key={current.id} className="animate-in fade-in slide-in-from-right-4 duration-150">
              <Card className="rounded-2xl shadow-sm" data-testid="fast-sign-case-card">
                <CardHeader className="pb-2">
                  <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                    <span data-testid="fast-sign-patient-name">{current.patient_name || 'Unnamed patient'}</span>
                    <Badge variant="outline" className="text-xs">{current.packet_label}</Badge>
                    {current.overdue && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Overdue {current.due_date}
                      </Badge>
                    )}
                    {(current.open_revision_count || 0) > 0 && (
                      <Badge className="text-xs bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]" data-testid="fast-sign-revision-badge">
                        <AlertTriangle className="h-3 w-3 mr-1" /> {questionOnly ? 'Question to patient' : 'Change requested'}
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="text-xs text-muted-foreground">
                    DOB {current.dob || '—'} · {current.employer_name || 'Employer —'}
                  </div>
                </CardHeader>
                <CardContent>
                  <FactRow label="Procedure" testId="fast-sign-fact-procedure">
                    {current.procedure || '—'}{current.severity ? ` · ${current.severity}` : ''}{current.icd10 ? ` · ${current.icd10}` : ''}{current.cpt ? ` · CPT ${current.cpt}` : ''}
                  </FactRow>
                  <FactRow label="Restrictions" testId="fast-sign-fact-restrictions">
                    {current.restrictions?.length
                      ? <ul className="space-y-0.5">{current.restrictions.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      : <span className="text-muted-foreground font-normal">None recorded</span>}
                  </FactRow>
                  <FactRow label="Return to work" testId="fast-sign-fact-rtw">
                    {current.rtw_weeks ? `~${current.rtw_weeks} weeks` : <span className="text-muted-foreground font-normal">Not estimated</span>}
                    {current.leave_start && <span className="block text-xs text-muted-foreground font-normal">Leave {current.leave_start} → {current.leave_end || 'TBD'}</span>}
                  </FactRow>
                  <FactRow label="Employer form" testId="fast-sign-fact-addendum">
                    {current.addendum ? (
                      <span className="inline-flex items-center gap-1.5">
                        <ScanLine className="h-3.5 w-3.5 text-primary" /> Scanned — filled below
                        {current.addendum_unanswered > 0 && (
                          <Badge variant="destructive" className="text-xs" data-testid="fast-sign-addendum-unanswered">{current.addendum_unanswered} unanswered</Badge>
                        )}
                      </span>
                    ) : <span className="text-muted-foreground font-normal">None</span>}
                  </FactRow>
                  {current.nurse_note && (
                    <FactRow label="Medical staff note" testId="fast-sign-fact-nurse-note">
                      <span className="font-normal">{current.nurse_note}</span>
                    </FactRow>
                  )}

                  {(!current.has_ai || current.ai_template || (current.ai_flags || []).length > 0 || current.addendum_unanswered > 0 || (current.open_revision_count || 0) > 0) && (
                    <div className="mt-3 rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] px-3 py-2 text-xs flex items-start gap-2" data-testid="fast-sign-warning">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div>
                        {(current.open_revision_count || 0) > 0 && (questionOnly
                          ? 'You asked the patient a question — fast-sign is disabled for this case. Open the full chart to see their answer. '
                          : `The patient requested ${current.open_revision_count} change(s) — fast-sign is disabled for this case. Open the full chart to review. `)}
                        {!current.has_ai && 'No AI recovery summary was generated for this case. '}
                        {/* Full-flow review 2026-09-06 §1.2: the nurse saw these flags; the
                            doctor's one-tap screen did not, and template text got signed. */}
                        {current.ai_template && (
                          <span data-testid="fast-sign-ai-template">
                            The recovery summary and restrictions are <b>standard template text</b> for this procedure — the AI was unavailable, so they were not written for this patient. Read them before signing.{' '}
                          </span>
                        )}
                        {current.addendum_unanswered > 0 && `${current.addendum_unanswered} employer-form question(s) are unanswered. `}
                        Consider opening the full chart before signing.
                        {(current.ai_flags || []).length > 0 && (
                          <ul className="list-disc pl-4 mt-1 space-y-0.5" data-testid="fast-sign-ai-flags">
                            {current.ai_flags.map((f, i) => <li key={i}>{f}</li>)}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/doctor/review/${current.id}`)} data-testid="doctor-fast-sign-open-chart-link">
                      <ExternalLink className="h-4 w-4 mr-1.5" /> Open full chart
                    </Button>
                    <span className="text-xs text-muted-foreground tabular-nums">Case {Math.min(idx + 1, queue.length)} of {queue.length}</span>
                  </div>
                </CardContent>
              </Card>
              {/* ADR 0006 decision 5: the employer's form, filled, inline above the
                  sign button — not forced. Amber marks an entry to glance at;
                  "Fix an answer" edits a value or moves an item to the extra
                  sheet, and the form is refilled on save. */}
              {current.addendum && (
                <Card className="rounded-2xl shadow-sm mt-4" data-testid="fast-sign-filled-form">
                  <CardContent className="pt-4">
                    <FilledFormPreview
                      sid={current.id}
                      onSaved={(r) => setQueue((qs) => qs.map((c) => (c.id === current.id
                        ? { ...c, addendum_unanswered: Array.isArray(r?.unanswered) ? r.unanswered.length : c.addendum_unanswered }
                        : c)))}
                    />
                  </CardContent>
                </Card>
              )}
          </div>
        </div>

        {/* ------- Signature rail (persists across cases) ------- */}
        <div className="space-y-4 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Session attestation</CardTitle></CardHeader>
            <CardContent>
              <label className="flex items-start gap-2.5 text-xs leading-5">
                <Checkbox checked={attest} onCheckedChange={(v) => setAttest(!!v)} className="mt-0.5" data-testid="doctor-fast-sign-attestation-checkbox" />
                <span>
                  For every case I sign in this session, I certify I have reviewed the patient information, clinical details, and recovery recommendations, and that the information is true and correct to the best of my professional medical knowledge.
                </span>
              </label>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm">{savedSig && !editingSig ? 'Your saved signature' : 'Signature (reused for each case)'}</CardTitle></CardHeader>
            <CardContent>
              {savedSig && !editingSig ? (
                <div className="space-y-2" data-testid="doctor-fast-sign-saved-signature">
                  <div className="rounded-xl border bg-white p-3">
                    <img src={savedSig} alt="Your saved signature" className="h-16 w-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Saved to your profile — it applies to every case you sign.</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingSig(true); setSignature(null); }} data-testid="doctor-fast-sign-change-signature">Change</Button>
                    <Button variant="ghost" size="sm" onClick={forgetSignature} data-testid="doctor-fast-sign-forget-signature">Forget</Button>
                  </div>
                </div>
              ) : (
                <>
                  <SignaturePad onChange={setSignature} defaultName={user?.name || ''} />
                  {savedSig && (
                    <Button variant="ghost" size="sm" className="mt-2" onClick={() => { setEditingSig(false); setSignature(savedSig); }} data-testid="doctor-fast-sign-keep-saved">Keep my saved signature</Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Button
            className="w-full h-14 rounded-xl text-base font-semibold"
            disabled={!canSign}
            onClick={signAndNext}
            data-testid="doctor-fast-sign-sign-next-button"
          >
            {busy ? 'Signing…' : <><Zap className="h-5 w-5 mr-1.5" /> Sign & next (Enter)</>}
          </Button>
          {!signature && <p className="text-xs text-muted-foreground text-center">Draw or type your signature once — it is saved to your profile and applies to every case you sign.</p>}
          {signature && !attest && <p className="text-xs text-muted-foreground text-center">Check the session attestation to enable signing.</p>}
          {signature && attest && revisionBlocked && (
            <p className="text-xs text-[hsl(32_92%_28%)] text-center" data-testid="fast-sign-revision-blocked-hint">
              {questionOnly
                ? 'You asked this patient a question — open the full chart (O) to see their answer.'
                : 'This patient requested a change — open the full chart (O) to review and resolve it.'}
            </p>
          )}
        </div>

        {/* Queue list */}
        <Card className="rounded-2xl lg:col-start-1 lg:row-start-2">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Up next</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="max-h-64">
                <ul className="space-y-1" data-testid="fast-sign-queue-list">
                  {queue.map((c, i) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setIdx(i)}
                        data-testid="doctor-fast-sign-queue-item"
                        className={cn(
                          'w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                          i === idx ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-card hover:bg-muted'
                        )}
                      >
                        <span className="font-medium truncate flex-1">{c.patient_name || 'Unnamed'}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[40%]">{c.procedure || '—'}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </CardContent>
        </Card>
      </div>
    </div>
  );
}
